import { useCallback } from 'react';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';

import { themeColors } from 'colors';

export type Props = {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    disabled: boolean;
};

const ChatComposer = ({ value, onChange, onSend, disabled }: Props) => {
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && !event.shiftKey && !disabled) {
                event.preventDefault();
                onSend();
            }
        },
        [onSend, disabled]
    );

    return (
        <Box
            role="region"
            aria-label="Message composer"
            data-testid="copy-generator-chat-composer"
            sx={{
                flexShrink: 0,
                px: 2,
                pb: 2,
                pt: 0,
            }}
        >
            <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                placeholder="Ask Sara to help you write"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={handleKeyDown}
                variant="outlined"
                size="small"
                inputProps={{
                    'aria-label': 'Message to Ask Sara',
                    'data-testid': 'copy-generator-chat-composer-input',
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 2.2, ml: 0, mr: 0.5 }}>
                            <IconButton
                                type="button"
                                aria-label="Send message"
                                data-testid="copy-generator-chat-send"
                                disabled={disabled}
                                onClick={onSend}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    p: 0,
                                    borderRadius: 1,
                                    color: themeColors.primary.contrastText,
                                    bgcolor: 'grey.900',
                                    '&:not(.Mui-disabled):hover': {
                                        bgcolor: 'grey.800',
                                    },
                                    '&.Mui-disabled': {
                                        color: themeColors.primary.contrastText,
                                        bgcolor: 'grey.400',
                                        opacity: 1,
                                    },
                                }}
                            >
                                <ArrowUpward sx={{ fontSize: 18 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        minHeight: 48,
                        alignItems: 'flex-start',
                        py: 0.75,
                        pr: 0.5,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'grey.300',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'grey.400',
                        },
                    },
                    '& .MuiInputBase-input': {
                        color: themeColors.text.primary,
                        fontSize: 14,
                        letterSpacing: '0.15px',
                        lineHeight: '20px',
                        py: 0.75,
                        '&::placeholder': {
                            color: themeColors.text.disabled,
                            opacity: 1,
                        },
                    },
                }}
            />
            <Typography
                color="text.secondary"
                sx={{ mt: 1, textAlign: 'center', fontSize: 12, letterSpacing: '0.4px' }}
                variant="caption"
            >
                AI can make mistakes. Review before sending. Learn more
            </Typography>
        </Box>
    );
};

export default ChatComposer;
